import { Dispatch, useCallback, useEffect } from 'react';
import {
  ConnectedConnections,
  ConnectionAction,
  IncomingConnections,
  OutgoingConnections,
} from './useUserConnection';
import {
  fetchConnectedUserConnection,
  fetchIncomingUserConnection,
  fetchOutgoingUserConnection,
} from '../service/ConnectionService';
import { UserConnection } from '../model/UserConnection';

export type FetchUserConnectionResult = {
  incomingConnections: {
    connections: UserConnection[];
    hasMore: boolean;
  };
  outgoingConnections: {
    connections: UserConnection[];
    hasMore: boolean;
  };
  connectedConnections: {
    connections: UserConnection[];
    hasMore: boolean;
  };
};

export const DEFAULT_CONNECTIONS_PAGE_SIZE = 20;

export const useFetchUserConnection = (
  dispatch: Dispatch<ConnectionAction>,
) => {
  useEffect(() => {
    const fetchData = async () => {
      dispatch({
        type: 'BEFORE_FETCH_CONNECTION',
      });
      const [incomingConnections, outgoingConnections, connectedConnections] =
        await Promise.all([
          fetchIncomingUserConnection(0, DEFAULT_CONNECTIONS_PAGE_SIZE),
          fetchOutgoingUserConnection(0, DEFAULT_CONNECTIONS_PAGE_SIZE),
          fetchConnectedUserConnection(0, DEFAULT_CONNECTIONS_PAGE_SIZE),
        ]);
      dispatch({
        type: 'FETCH_CONNECTION',
        payload: {
          incomingConnections: {
            connections: incomingConnections,
            hasMore:
              incomingConnections.length === DEFAULT_CONNECTIONS_PAGE_SIZE,
          },
          outgoingConnections: {
            connections: outgoingConnections,
            hasMore:
              outgoingConnections.length === DEFAULT_CONNECTIONS_PAGE_SIZE,
          },
          connectedConnections: {
            connections: connectedConnections,
            hasMore:
              connectedConnections.length === DEFAULT_CONNECTIONS_PAGE_SIZE,
          },
        },
      });
    };
    fetchData();
  }, [dispatch]);
};

export const useFetchMoreConnectedConnections = (
  connectedConnections: ConnectedConnections,
  connectionDispatch: Dispatch<ConnectionAction>,
) => {
  return useCallback(async () => {
    connectionDispatch({
      type: 'BEFORE_FETCH_MORE_CONNECTED_CONNECTION',
    });
    const connections = await fetchConnectedUserConnection(
      connectedConnections.page + 1,
      DEFAULT_CONNECTIONS_PAGE_SIZE,
    );
    connectionDispatch({
      type: 'FETCH_MORE_CONNECTED_CONNECTIONS',
      payload: {
        connections: connections,
        hasMore: connections.length === DEFAULT_CONNECTIONS_PAGE_SIZE,
        loading: false,
        page: connectedConnections.page + 1,
      },
    });
  }, [connectedConnections.page, connectionDispatch]);
};

export const useFetchMoreIncomingConnections = (
  incomingConnections: IncomingConnections,
  connectionDispatch: Dispatch<ConnectionAction>,
) => {
  return useCallback(async () => {
    connectionDispatch({
      type: 'BEFORE_FETCH_MORE_INCOMING_CONNECTION',
    });
    const connections = await fetchIncomingUserConnection(
      incomingConnections.page + 1,
      DEFAULT_CONNECTIONS_PAGE_SIZE,
    );
    connectionDispatch({
      type: 'FETCH_MORE_INCOMING_CONNECTIONS',
      payload: {
        connections: connections,
        hasMore: connections.length === DEFAULT_CONNECTIONS_PAGE_SIZE,
        loading: false,
        page: incomingConnections.page + 1,
      },
    });
  }, [incomingConnections.page, connectionDispatch]);
};

export const useFetchMoreOutgoingConnections = (
  outgoingConnections: OutgoingConnections,
  connectionDispatch: Dispatch<ConnectionAction>,
) => {
  return useCallback(async () => {
    connectionDispatch({
      type: 'BEFORE_FETCH_MORE_OUTGOING_CONNECTION',
    });
    const connections = await fetchOutgoingUserConnection(
      outgoingConnections.page + 1,
      DEFAULT_CONNECTIONS_PAGE_SIZE,
    );
    connectionDispatch({
      type: 'FETCH_MORE_OUTGOING_CONNECTIONS',
      payload: {
        connections: connections,
        hasMore: connections.length === DEFAULT_CONNECTIONS_PAGE_SIZE,
        loading: false,
        page: outgoingConnections.page + 1,
      },
    });
  }, [outgoingConnections.page, connectionDispatch]);
};
